import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Loader2, Check, Circle } from 'lucide-react';

const STAGES = [
  { label: 'Validating credits & preparing design brief', at: 5 },
  { label: 'Analyzing attachments & game context', at: 12 },
  { label: 'AI model writing game code', at: 20 },
  { label: 'Validating generated code', at: 85 },
  { label: 'Building playable preview', at: 92 },
];

const StageRow = ({ label, state }) => (
  <li className="flex items-center gap-2 text-xs">
    {state === 'done' && <Check className="h-3.5 w-3.5 text-green-400 shrink-0" />}
    {state === 'current' && <Loader2 className="h-3.5 w-3.5 text-purple-400 animate-spin shrink-0" />}
    {state === 'pending' && <Circle className="h-2.5 w-2.5 text-gray-600 shrink-0 ml-0.5 mr-0.5" />}
    <span className={state === 'done' ? 'text-gray-500 line-through' : state === 'current' ? 'text-gray-100' : 'text-gray-500'}>
      {label}
    </span>
  </li>
);

export const ProgressDialog = ({ open, jobStatus, onCancel }) => {
  const progress = jobStatus?.progress ?? 2;
  const stage = jobStatus?.stage || 'Queued';
  const detail = jobStatus?.detail || '';

  const stageState = (idx) => {
    const start = STAGES[idx].at;
    const next = idx + 1 < STAGES.length ? STAGES[idx + 1].at : 100;
    if (progress >= next) return 'done';
    if (progress >= start) return 'current';
    return 'pending';
  };

  return (
    <Dialog open={open}>
      <DialogContent data-testid="progress-dialog" className="bg-gray-900 border border-purple-500/40 max-w-md shadow-2xl shadow-black/80">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-purple-400" />
              GameOS Agent is building your game
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="progress-dialog-cancel-btn"
              onClick={onCancel}
              className="border-red-500 text-red-400 hover:bg-red-500/10 text-xs"
            >
              Cancel
            </Button>
          </DialogTitle>
          <DialogDescription className="text-gray-500 text-xs">
            Live build status — builds run safely in the background, even for very large games.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-1 text-sm text-gray-300 space-y-4">
          <div>
            <p data-testid="progress-stage-text" className="mb-1 font-medium text-white">{stage}</p>
            {detail && (
              <p data-testid="progress-detail-text" className="text-xs text-purple-300">{detail}</p>
            )}
          </div>
          <div>
            <Progress value={progress} className="h-2" />
            <p className="mt-1 text-xs text-gray-400">{progress}% complete</p>
          </div>
          <ul className="space-y-1.5" data-testid="progress-stage-list">
            {STAGES.map((s, i) => (
              <StageRow key={s.label} label={s.label} state={stageState(i)} />
            ))}
          </ul>
          <p className="text-[11px] text-gray-500">
            Build time scales with your prompt's complexity — simple games finish in under a minute, while
            ambitious 3D or multi-level builds can take considerably longer. Feel free to keep this running;
            cancelling never costs credits.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
